import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { 
  LayoutDashboard, Users, Send, MessageSquare, User, BarChart3, 
  LogOut, Network, Bell, Search, Plus, ArrowUpRight, ArrowDownLeft,
  Clock, CheckCircle, AlertCircle, ChevronRight, Menu, X,
  Calendar, Database, Shield, CreditCard, Sparkles
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import PromoCodeCard from "../components/PromoCodeCard";
import { useAuth, API } from "../App";
import { toast } from "sonner";

// Sidebar Component
export const Sidebar = ({ currentPath }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  const navItems = [
    { icon: LayoutDashboard, label: "Dashboard", path: "/dashboard" },
    { icon: Users, label: "Directory", path: "/directory" },
    { icon: Send, label: "Referrals", path: "/referrals" },
    { icon: Calendar, label: "Meetings", path: "/meetings" },
    { icon: MessageSquare, label: "Messages", path: "/messages" },
    { icon: Database, label: "Provider Search", path: "/providers" },
    { icon: BarChart3, label: "Analytics", path: "/analytics" },
    { icon: Shield, label: "Admin", path: "/admin" },
    { icon: User, label: "Profile", path: "/profile" },
  ];

  const handleLogout = async () => {
    await logout();
    navigate("/");
    toast.success("Logged out successfully");
  };

  const SidebarContent = () => (
    <>
      <div className="p-6 border-b border-slate-200">
        <Link to="/dashboard" className="flex items-center gap-2">
          <img 
            src="https://customer-assets.emergentagent.com/job_medical-referral-hub/artifacts/m1ei4qxx_IMG_5363.jpeg" 
            alt="iConcierge Logo" 
            className="h-10 w-auto"
          />
        </Link>
      </div>

      <nav className="flex-1 p-4 space-y-1">
        {navItems.map((item) => (
          <button
            key={item.path}
            onClick={() => {
              navigate(item.path);
              setMobileOpen(false);
            }}
            className={`sidebar-item w-full ${currentPath === item.path ? 'active' : ''}`}
            data-testid={`nav-${item.label.toLowerCase()}`}
          >
            <item.icon className="w-5 h-5" />
            {item.label}
          </button>
        ))}
      </nav>

      <div className="p-4 border-t border-slate-200">
        <div className="flex items-center gap-3 mb-4 px-4">
          <Avatar className="w-10 h-10">
            <AvatarImage src={user?.picture} />
            <AvatarFallback className="bg-primary/10 text-primary">
              {user?.name?.charAt(0) || 'U'}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-slate-900 truncate">{user?.name}</p>
            <p className="text-sm text-slate-500 truncate">{user?.email}</p>
          </div>
        </div>
        <Button 
          variant="ghost" 
          className="w-full justify-start text-slate-600 hover:text-red-600 hover:bg-red-50"
          onClick={handleLogout}
          data-testid="logout-btn"
        >
          <LogOut className="w-5 h-5 mr-2" />
          Sign Out
        </Button>
      </div>
    </>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex flex-col w-64 bg-white border-r border-slate-200 h-screen fixed left-0 top-0">
        <SidebarContent />
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 glassmorphism h-16 flex items-center justify-between px-4">
        <Link to="/dashboard" className="flex items-center gap-2">
          <img 
            src="https://customer-assets.emergentagent.com/job_medical-referral-hub/artifacts/m1ei4qxx_IMG_5363.jpeg" 
            alt="iConcierge Logo" 
            className="h-8 w-auto"
          />
        </Link>
        <button 
          onClick={() => setMobileOpen(!mobileOpen)}
          className="p-2 hover:bg-slate-100 rounded-lg"
          data-testid="mobile-nav-toggle"
        >
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </header>

      {/* Mobile Sidebar Overlay */}
      {mobileOpen && (
        <>
          <div 
            className="lg:hidden fixed inset-0 bg-black/50 z-40"
            onClick={() => setMobileOpen(false)}
          />
          <aside className="lg:hidden fixed left-0 top-0 bottom-0 w-64 bg-white z-50 flex flex-col">
            <SidebarContent />
          </aside>
        </>
      )}
    </>
  );
};

// Main Dashboard Page
const Dashboard = () => {
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const isWelcome = searchParams.get("welcome") === "true";
  const [dashboardData, setDashboardData] = useState(null);
  const [showMembershipPrompt, setShowMembershipPrompt] = useState(isWelcome);
  const [loading, setLoading] = useState(true);

  const fetchDashboard = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/dashboard`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setDashboardData(response.data);
    } catch (error) {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const getStatusBadge = (status) => {
    const styles = {
      pending: "badge-pending",
      accepted: "badge-accepted",
      declined: "badge-declined",
      completed: "badge-completed"
    };
    return styles[status] || "bg-slate-100 text-slate-700";
  };

  const getUrgencyStyle = (urgency) => {
    const styles = {
      routine: "urgency-routine",
      urgent: "urgency-urgent",
      emergency: "urgency-emergency"
    };
    return styles[urgency] || "";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 lg:pl-64">
        <Sidebar currentPath={location.pathname} />
        <main className="pt-20 lg:pt-8 p-4 lg:p-8">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white rounded-xl border border-slate-200 p-6 animate-pulse">
                  <div className="h-4 bg-slate-200 rounded w-1/2 mb-4"></div>
                  <div className="h-8 bg-slate-200 rounded w-1/3"></div>
                </div>
              ))}
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 lg:pl-64">
      <Sidebar currentPath={location.pathname} />
      
      <main className="pt-20 lg:pt-8 p-4 lg:p-8" data-testid="dashboard-main">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                Welcome back, Dr. {user?.name?.split(' ')[0] || 'Physician'}
              </h1>
              <p className="text-slate-600 mt-1">Here's what's happening with your network</p>
            </div>
            <Button 
              onClick={() => navigate("/referrals")}
              className="bg-primary text-white hover:bg-primary/90 rounded-full"
              data-testid="new-referral-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Referral
            </Button>
          </div>

          {/* Welcome Trial Banner */}
          {showMembershipPrompt && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gradient-to-r from-emerald-50 to-blue-50 border border-emerald-200 rounded-xl p-6 mb-6"
              data-testid="membership-prompt"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-6 h-6 text-emerald-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-slate-900 mb-1">Welcome to iConcierge! Your 30-day free trial is active.</h3>
                  <p className="text-slate-600 mb-3">
                    You have full access to all features — schedule meetings, send unlimited messages, manage referrals, and grow your network. No credit card required.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <Button
                      onClick={() => navigate("/browse")}
                      className="bg-primary text-white hover:bg-primary/90 rounded-full"
                    >
                      Browse Providers
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => navigate("/profile")}
                      className="text-slate-600"
                    >
                      Complete Your Profile
                    </Button>
                  </div>
                </div>
                <button
                  onClick={() => setShowMembershipPrompt(false)}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </motion.div>
          )}

          {/* Promo Code Banner — always available to redeem */}
          <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6" data-testid="dashboard-promo-section">
            <div className="flex items-center gap-2 text-sm text-slate-700 mb-1">
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="font-medium">Beta tester or VIP?</span>
              <span className="text-slate-500">Redeem your promo code for extended free access.</span>
            </div>
            <PromoCodeCard variant="banner" />
          </div>

          {/* Profile Alert */}
          {dashboardData?.profile && !dashboardData.profile.profile_complete && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-center gap-3"
            >
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-amber-800 font-medium">Complete your profile</p>
                <p className="text-amber-700 text-sm">Add your specialty and practice details to appear in the directory.</p>
              </div>
              <Button 
                size="sm" 
                variant="outline" 
                onClick={() => navigate("/profile")}
                className="border-amber-300 text-amber-700 hover:bg-amber-100"
              >
                Complete Profile
              </Button>
            </motion.div>
          )}

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="stat-card"
            >
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-medium text-slate-500 uppercase tracking-wider">Referrals Sent</span>
                <ArrowUpRight className="w-5 h-5 text-primary" />
              </div>
              <div className="text-3xl font-bold text-slate-900">{dashboardData?.stats?.referrals_sent || 0}</div>
              <p className="text-sm text-slate-500 mt-1">Total sent</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="stat-card"
            >
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-medium text-slate-500 uppercase tracking-wider">Referrals Received</span>
                <ArrowDownLeft className="w-5 h-5 text-emerald-500" />
              </div>
              <div className="text-3xl font-bold text-slate-900">{dashboardData?.stats?.referrals_received || 0}</div>
              <p className="text-sm text-slate-500 mt-1">Total received</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="stat-card"
            >
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-medium text-slate-500 uppercase tracking-wider">Pending</span>
                <Clock className="w-5 h-5 text-amber-500" />
              </div>
              <div className="text-3xl font-bold text-slate-900">{dashboardData?.pending_referrals || 0}</div>
              <p className="text-sm text-slate-500 mt-1">Awaiting response</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="stat-card"
            >
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-medium text-slate-500 uppercase tracking-wider">Unread Messages</span>
                <MessageSquare className="w-5 h-5 text-blue-500" />
              </div>
              <div className="text-3xl font-bold text-slate-900">{dashboardData?.unread_messages || 0}</div>
              <p className="text-sm text-slate-500 mt-1">New messages</p>
            </motion.div>
          </div>

          {/* Recent Activity */}
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Recent Referrals */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white rounded-xl border border-slate-200 p-6"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-slate-900">Recent Referrals Received</h2>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => navigate("/referrals")}
                  className="text-primary"
                >
                  View All
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>

              {dashboardData?.recent_referrals?.length > 0 ? (
                <div className="space-y-4">
                  {dashboardData.recent_referrals.map((referral, index) => (
                    <div 
                      key={referral.referral_id}
                      className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
                      onClick={() => navigate("/referrals")}
                    >
                      <Avatar className="w-10 h-10">
                        <AvatarImage src={referral.from_physician?.picture} />
                        <AvatarFallback className="bg-primary/10 text-primary">
                          {referral.from_physician?.name?.charAt(0) || 'P'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-slate-900">{referral.from_physician?.name || 'Unknown'}</span>
                          <Badge className={getStatusBadge(referral.status)}>
                            {referral.status}
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-600 truncate">Patient: {referral.patient_name}</p>
                        <p className={`text-sm ${getUrgencyStyle(referral.urgency)}`}>{referral.urgency}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Send className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No referrals received yet</p>
                  <p className="text-sm">Share your profile to start receiving referrals</p>
                </div>
              )}
            </motion.div>

            {/* Recent Leads */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-white rounded-xl border border-slate-200 p-6"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-slate-900">Recent Leads</h2>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => navigate("/analytics")}
                  className="text-primary"
                >
                  View All
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>

              {dashboardData?.recent_leads?.length > 0 ? (
                <div className="space-y-4">
                  {dashboardData.recent_leads.map((lead) => (
                    <div 
                      key={lead.lead_id}
                      className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg"
                    >
                      <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900">{lead.patient_name}</p>
                        <p className="text-sm text-slate-500">Source: {lead.source}</p>
                      </div>
                      <Badge className={
                        lead.status === 'converted' ? 'badge-accepted' :
                        lead.status === 'new' ? 'badge-pending' :
                        'bg-slate-100 text-slate-700'
                      }>
                        {lead.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No leads yet</p>
                  <p className="text-sm">Leads from referrals and campaigns will appear here</p>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
